import { ZoomTracker } from '@zoom/zdt_events';
import { UserEntity } from '@zoom/zdt_events/dist/entities/zoom_billing_v2/User';
import { AppErrorEntity } from '@zoom/zdt_events/dist/entities/zoom_billing_v2/AppError'
import { LogTraceEvent } from '@zoom/zdt_events/dist/events/zoom_billing_v2/LogTrace';
import { getSnowplowConfig } from '@/apis';
import store from '@/store/index';
import { debounce } from 'lodash';
import { logger } from '@/utils/log';

/**
 * IMPORTANT NOTICE:
 * When using Snowplow for tracking, be aware that due to the debounce mechanism,
 * tracking events might be lost when a user clicks a button that triggers a page navigation.
 * To prevent this, ensure the event is sent before navigating to the new page.
 */

const COLLECTOR_NAME = 'zoom_billing_v2';
const FEATURE_NAME = 'fe_billing_one_page_v2'
const FE_ERROR = 'fe_error'

const EVENT_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
};

export const SNOWPLOW_TRACKING_LOG_LEVEL = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  DEBUG: 'debug',
  TRACE: 'trace',
};

class SnowPlowTrackingQueue {
  isReady = false;
  isEnable;
  eventList = [];
  debounceTime = 800;
  static configuration;

  constructor(isEnable) {
    this.isEnable = isEnable;
  }
  async init() {
    if (!this.isEnable) {
      return false;
    }
    SnowPlowTrackingQueue.configuration = SnowPlowTrackingQueue.configuration || (await this.getConfiguration());
    logger.log('Get Snowplow API data', SnowPlowTrackingQueue.configuration);
    const { collectionUrl, accountEnableNSNToggle, userEnableNSNToggle } = SnowPlowTrackingQueue.configuration;

    const globalEntity = {
      userEntity: entityCreator.user(),
    };

    ZoomTracker.init({
      collector: {
        url: collectionUrl,
        name: COLLECTOR_NAME,
      },
      contexts: globalEntity,
      options: {
        dataCollectionFlag: accountEnableNSNToggle && userEnableNSNToggle,
      },
    });
    this.isReady = true;
    logger.log('Success init Snowplow Tracker.');
  }
  getConfiguration() {
    function formatData(data) {
      if (data.collectionUrl && !/^https?:\/\//.test(data.collectionUrl)) {
        // prepend https:// protocol
        data.collectionUrl = 'https://' + data.collectionUrl;
      }
      return data;
    }
    return new Promise((resolve) => {
      getSnowplowConfig().then((res) => {
        resolve(formatData(res));
      });
    });
  }
  sendEventDebounce(...events) {
    if (!this.isEnable) {
      return false;
    }
    if (ZoomTracker) {
      try {
        let eventList = events;
        logger.log('Push Snowplow tracking events to queue', ...events);
        this.pushEventToQueue(eventList);
        this.debounceFlushPendingEvent();
      } catch (err) {
        logger.warn(err);
      }
    }
  }
  pushEventToQueue(eventList = []) {
    eventList.forEach((event) => {
      this.eventList.push({ data: event, status: EVENT_STATUS.PENDING });
    });
  }
  debounceFlushPendingEvent = debounce(async () => {
    if (!this.isReady) {
      await this.init();
    }
    const pendingEvents = this.getPendingEvent();
    const snowplowEvents = this.getPendingEvent().map((item) => {
      item.status = EVENT_STATUS.SENT;
      return item.data;
    });
    logger.log('Sending Snowplow tracking events', ...pendingEvents.map((item) => item.data));

    try {
      ZoomTracker?.track(snowplowEvents);
    } catch (err) {
      logger.warn(err);
    }
  }, this.debounceTime);
  getPendingEvent() {
    return this.eventList.filter(({ status }) => status === EVENT_STATUS.PENDING);
  }
}

const SNOWPLOW_EVENT_KEYS = {
  ACCOUNT_ID: 'accountId',
  EMAIL_ID: 'emailId',
};

const entityCreator = {
  user: () => {
    const { lookupInfo, account } = store.getters;
    return Object.assign(new UserEntity(), {
      [SNOWPLOW_EVENT_KEYS.ACCOUNT_ID]: lookupInfo?.accountId || '',
      [SNOWPLOW_EVENT_KEYS.EMAIL_ID]: account?.email || '',
    });
  },
};

let eventTracking;

function sendEventInQueue(...events) {
  if (!eventTracking) {
    eventTracking = new SnowPlowTrackingQueue(true);
  }
  eventTracking.sendEventDebounce(...events);
}

export const snowPlowTrackingHandler = {
  sendLog: ({ logLevel = SNOWPLOW_TRACKING_LOG_LEVEL.ERROR, message = '', name = 'opc fe v2'}) => {
    const logTraceProperties = {
      logName: FE_ERROR,
      logLevel,
      featureName: FEATURE_NAME,
    };
    const appErrorEntity = Object.assign(new AppErrorEntity(), {
      exceptionName: name,
      stackTrace: message
    })
    const logTraceEvent = Object.assign(new LogTraceEvent(), {
      ...logTraceProperties,
      ...{ appErrorEntity }
    });
    sendEventInQueue(logTraceEvent);
  },
};
